import { useTranslate } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { FileTextOutlined, RightOutlined, SyncOutlined } from "@ant-design/icons";
import { Tooltip, theme } from "antd";
import { GlassCard, ActionButton } from "../../../shared/components";
import { hexToRgba, usePageStyles } from "../../../shared/styles";
import { useColorMode } from "../../../contexts/color-mode";
import { useNavigate } from "react-router-dom";
import type { CSSProperties } from "react";
import type { StatConfig } from "../types";

export interface RecentRequestItem {
    id: number;
    status: string;
    createdAt: string;
    make?: string;
    model?: string;
    vin?: string;
    products?: Array<{ name: string }>;
}

export interface RecentRequestsCardProps {
    requests: RecentRequestItem[];
    statusCounts: StatConfig[];
    listUrl: string;
    accountId: number;
    hoveredRequest: number | null;
    onHoverRequest: (id: number | null) => void;
}

export function RecentRequestsCard({
    requests,
    statusCounts,
    listUrl,
    accountId,
    hoveredRequest,
    onHoverRequest,
}: RecentRequestsCardProps) {
    const _t = useTranslate();
    const navigate = useNavigate();
    const styles = usePageStyles();
    const { mode } = useColorMode();
    const { token } = theme.useToken();
    const isDark = mode === "dark";

    const getStatusStyle = (status: string) => {
        if (status === "completed") {
            return {
                bg: hexToRgba(token.colorSuccess, 0.1),
                color: token.colorSuccess,
                border: hexToRgba(token.colorSuccess, 0.2),
            };
        }
        return {
            bg: hexToRgba(token.colorInfo, 0.1),
            color: token.colorInfo,
            border: hexToRgba(token.colorInfo, 0.2),
        };
    };

    return (
        <GlassCard variant="purple">
            <div className="flex justify-between items-center mb-5">
                <div className="flex items-center gap-3">
                    <div style={styles.cardIcon("purple")}>
                        <FileTextOutlined className="text-xl" />
                    </div>
                    <h3 className="m-0 text-base font-semibold" style={{ color: styles.container.color }}>
                        {_t("orders.titles.recentlist", "Dernières demandes")}
                    </h3>
                </div>
                <ActionButton variant="secondary" onClick={() => navigate(listUrl)} icon={<RightOutlined />}>
                    {_t("buttons.showAll", "Voir tout")}
                </ActionButton>
            </div>

            <div className="flex flex-col gap-3">
                {requests.slice(0, 3).map((request) => (
                    <div
                        key={request.id}
                        onMouseEnter={() => onHoverRequest(request.id)}
                        onMouseLeave={() => onHoverRequest(null)}
                        className="rounded-2xl px-5 py-5 cursor-pointer transition-all duration-200"
                        style={{
                            background:
                                hoveredRequest === request.id
                                    ? isDark
                                        ? "rgba(255,255,255,0.04)"
                                        : "rgba(0,0,0,0.04)"
                                    : isDark
                                      ? "rgba(255,255,255,0.02)"
                                      : "rgba(0,0,0,0.02)",
                            border: `1px solid ${
                                hoveredRequest === request.id
                                    ? isDark
                                        ? "rgba(255,255,255,0.1)"
                                        : "rgba(0,0,0,0.1)"
                                    : isDark
                                      ? "rgba(255,255,255,0.04)"
                                      : "rgba(0,0,0,0.04)"
                            }`,
                        }}
                        onClick={() => navigate(`/accounts/${accountId}/orders/show/${request.id}`)}
                    >
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <p className="m-0 mb-1 text-[15px] font-semibold" style={{ color: styles.container.color }}>
                                    {request.make || ""} {request.model || ""}
                                </p>
                                <p
                                    className="m-0 text-xs font-mono"
                                    style={{
                                        color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                                    }}
                                >
                                    {request.vin || ""}
                                </p>
                            </div>
                            <span
                                className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold border"
                                style={{
                                    background: getStatusStyle(request.status).bg,
                                    color: getStatusStyle(request.status).color,
                                    borderColor: getStatusStyle(request.status).border,
                                }}
                            >
                                {request.status === "ongoing" && <SyncOutlined spin className="text-xs" />}
                                {request.status === "completed" && "✓"}
                                {statusCounts.find((s) => s.key === request.status)?.label || request.status}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-4">
                                {request.products && request.products.length > 0 && (() => {
                                    const productName = request.products[0]?.name || "";
                                    const truncatedName = productName.length > 20 ? `${productName.slice(0, 20)}...` : productName;
                                    const productPillStyle: CSSProperties = {
                                        padding: "2px 10px",
                                        borderRadius: 6,
                                        fontSize: 12,
                                        fontWeight: 500,
                                        background: styles.primaryRgba(0.1),
                                        color: styles.primaryColor,
                                    };
                                    return productName.length > 20 ? (
                                        <Tooltip title={productName}>
                                            <span className="cursor-help" style={productPillStyle}>
                                                {truncatedName}
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <span style={productPillStyle}>
                                            {productName}
                                        </span>
                                    );
                                })()}
                                <span
                                    className="text-xs"
                                    style={{
                                        color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                                    }}
                                >
                                    <DateField value={request.createdAt} format="DD MMM HH:mm" />
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </GlassCard>
    );
}
